/**
 * Gössl Theme - Cart Page
 * Handles quantity updates and item removal via AJAX.
 */

(function() {
  'use strict';

  // r162: cap every cart request (see cart-drawer.js). Older WebKit without
  // AbortSignal.timeout simply runs without a cap.
  const timeoutSignal = (ms) => (typeof AbortSignal !== 'undefined' && AbortSignal.timeout) ? AbortSignal.timeout(ms) : undefined;

  class CartPage {
    constructor() {
      // r188: the cart section only. The old '[data-cart]' fallback matched the hidden „Weitermachen" shell
      // (sections/resume-strip-2026.liquid, data-cart = item count), which sits before <main> on every page -
      // the +, − and remove buttons were bound to it and did nothing on the cart page.
      this.cart = document.querySelector('[data-cart-page]');
      if (!this.cart) return;

      this.itemsContainer = this.cart.querySelector('[data-cart-items]');
      this.subtotalElement = document.querySelector('[data-cart-subtotal]');
      this.totalElement = document.querySelector('[data-cart-total]');
      this.checkoutButton = document.querySelector('[data-checkout-button]');
      this.emptyMessage = document.querySelector('[data-cart-empty]');
      this.cartContent = document.querySelector('[data-cart-content]');

      this.bindEvents();
      this.showPendingNotice();
    }

    // r188: the note left by a pre-order split before the reload (see updateQuantity)
    showPendingNotice() {
      let message = null;
      try { message = sessionStorage.getItem('g26_cart_notice'); sessionStorage.removeItem('g26_cart_notice'); } catch (e) { return; }
      if (!message || !this.itemsContainer) return;
      const note = document.createElement('p');
      note.className = 'g26-cart__notice';
      note.setAttribute('role', 'status');
      note.textContent = message;
      this.itemsContainer.parentNode.insertBefore(note, this.itemsContainer);
    }

    bindEvents() {
      // Quantity buttons
      this.cart.addEventListener('click', (e) => {
        const minusBtn = e.target.closest('[data-quantity-minus]');
        const plusBtn = e.target.closest('[data-quantity-plus]');
        const removeBtn = e.target.closest('[data-remove-item]');

        if (minusBtn) {
          e.preventDefault();
          this.adjustQuantity(minusBtn, -1);
        } else if (plusBtn) {
          e.preventDefault();
          this.adjustQuantity(plusBtn, 1);
        } else if (removeBtn) {
          e.preventDefault();
          this.removeItem(removeBtn);
        }
      });

      // r162: a cart page restored from Safari's back-forward cache shows
      // lines the server no longer has (or misses new ones) - reload to the
      // real cart instead of letting the customer fight a stale page.
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) window.location.reload();
      });

      // r190: a line got another colour or size (assets/cart-line-options.js) - its whole row changes (price,
      // notes, maybe a pre-order split), so reload; a failure note survives the reload like the r188 split note
      document.addEventListener('cart:line-swapped', (e) => {
        const error = e.detail && e.detail.error;
        if (error) { try { sessionStorage.setItem('g26_cart_notice', error); } catch (err) { /* the reload still shows the real cart */ } }
        window.location.reload();
      });

      // r188: the form's own buttons (Zur Kasse, Warenkorb aktualisieren) - see onSubmit
      const form = this.cart.querySelector('[data-cart-form]');
      if (form) form.addEventListener('submit', (e) => this.onSubmit(e));

      // Direct quantity input change
      this.cart.addEventListener('change', (e) => {
        if (e.target.matches('[data-quantity-input]')) {
          this.updateQuantity(e.target);
        }
      });

      // Lieblingsgeschäft select — persist immediately on change so the
      // attribute reaches Shopify's cart regardless of whether the customer
      // clicks "Warenkorb aktualisieren" before checkout.
      const favStoreSelect = document.querySelector('#cart-lieblingsgeschaeft');
      if (favStoreSelect) {
        favStoreSelect.addEventListener('change', () => {
          this.persistCartAttribute('lieblingsgeschaeft', favStoreSelect.value);
        });
      }
    }

    // r188: the checkout door of the cart page. The form posts updates[] straight to Shopify, so a quantity
    // typed and sent with „Zur Kasse" would skip the cap. Apply the form (quantities, note, Lieblingsgeschäft)
    // through /cart/update.js, cap, and only then go where the button points; when pieces moved onto a
    // pre-order line the customer stays and sees it before paying. The form is never posted a second time:
    // its updates[] are positional and would hit the wrong lines once a 0 had removed one. If the update
    // fails, the plain form post runs as before r188.
    async onSubmit(e) {
      if (this.nativeSubmit || !window.g26preorder || !window.g26preorder.capCart || !window.URLSearchParams) return;
      e.preventDefault();
      if (this.submitting) return;
      this.submitting = true;
      const form = e.target;
      const submitter = e.submitter || null;
      const moves = window.g26preorder.moves || 0;
      try {
        const response = await fetch(window.g26url('cart/update.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
          body: new URLSearchParams(new FormData(form)).toString(),
          signal: timeoutSignal(15000)
        });
        if (!response.ok) throw new Error('Failed to update cart');
        const capped = await window.g26preorder.capCart();
        if (capped.moved || (window.g26preorder.moves || 0) !== moves) {
          try { sessionStorage.setItem('g26_cart_notice', capped.message || window.g26preorder.pendingNotice || ''); } catch (err) { /* the reload still shows both lines */ }
          window.location.reload();
          return;
        }
        if (submitter && submitter.name === 'checkout') window.location.href = window.g26url('checkout');
        else window.location.reload();
      } catch (error) {
        console.error('Cart submit error:', error);
        this.nativeSubmit = true;
        if (form.requestSubmit) { form.requestSubmit(submitter); return; }
        if (submitter && submitter.name) {
          const hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = submitter.name;
          hidden.value = submitter.value || '';
          form.appendChild(hidden);
        }
        form.submit();
      }
    }

    async persistCartAttribute(key, value) {
      try {
        const response = await fetch(window.g26url('cart/update.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { [key]: value } })
        });
        if (!response.ok) throw new Error('Failed to persist cart attribute');
      } catch (error) {
        console.error('Error persisting cart attribute', key, error);
      }
    }

    adjustQuantity(button, change) {
      const item = button.closest('[data-cart-item]');
      const input = item.querySelector('[data-quantity-input]');
      const currentValue = parseInt(input.value, 10);
      const newValue = Math.max(0, currentValue + change);

      if (newValue === 0) {
        this.removeItem(button);
      } else {
        input.value = newValue;
        this.updateQuantity(input);
      }
    }

    async updateQuantity(input) {
      const item = input.closest('[data-cart-item]');
      const key = item.dataset.cartItem;
      const quantity = parseInt(input.value, 10);

      if (quantity < 1) {
        this.removeItem(input);
        return;
      }

      item.classList.add('is-updating');

      try {
        const response = await fetch(window.g26url('cart/change.js'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: timeoutSignal(15000),
          body: JSON.stringify({
            id: key,
            quantity: quantity
          })
        });

        if (!response.ok) {
          throw new Error('Failed to update cart');
        }

        const cart = await response.json();

        // r188: + on a line sold from stock never goes past that stock - the extra pieces move onto a
        // pre-order line (assets/preorder-cart.js); reload so both lines show, the note survives it
        if (window.g26preorder && window.g26preorder.capCart) {
          const capped = await window.g26preorder.capCart();
          if (capped.moved) {
            try { sessionStorage.setItem('g26_cart_notice', capped.message); } catch (e) { /* the reload still shows both lines */ }
            window.location.reload();
            return;
          }
        }

        this.updateCartUI(cart);
        this.updateCartCount(cart.item_count);

        // Update line item price
        const lineItem = cart.items.find(i => i.key === key);
        if (lineItem) {
          const priceElement = item.querySelector('[data-line-price]');
          if (priceElement) {
            priceElement.textContent = this.formatMoney(lineItem.final_line_price);
          }
        }

      } catch (error) {
        console.error('Error updating quantity:', error);
        // Revert to previous value by refreshing the page
        window.location.reload();
      } finally {
        item.classList.remove('is-updating');
      }
    }

    async removeItem(trigger) {
      const item = trigger.closest('[data-cart-item]');
      const key = item.dataset.cartItem;

      item.classList.add('is-removing');

      try {
        const response = await fetch(window.g26url('cart/change.js'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: timeoutSignal(15000),
          body: JSON.stringify({
            id: key,
            quantity: 0
          })
        });

        if (!response.ok) {
          throw new Error('Failed to remove item');
        }

        const cart = await response.json();

        // Animate removal
        item.style.height = item.offsetHeight + 'px';
        item.offsetHeight; // Force reflow
        item.style.height = '0';
        item.style.opacity = '0';
        item.style.marginBottom = '0';
        item.style.paddingTop = '0';
        item.style.paddingBottom = '0';

        setTimeout(() => {
          item.remove();
          this.updateCartUI(cart);
          this.updateCartCount(cart.item_count);

          // Show empty message if cart is empty
          if (cart.item_count === 0) {
            this.showEmptyCart();
          }
        }, 300);

      } catch (error) {
        console.error('Error removing item:', error);
        item.classList.remove('is-removing');
        window.location.reload();
      }
    }

    updateCartUI(cart) {
      // Update subtotal and the estimated total (r189: the total kept the old amount until a reload)
      if (this.subtotalElement) {
        this.subtotalElement.textContent = this.formatMoney(cart.total_price);
      }
      if (this.totalElement) {
        this.totalElement.textContent = this.formatMoney(cart.total_price);
      }

      // Update checkout button state
      if (this.checkoutButton) {
        this.checkoutButton.disabled = cart.item_count === 0;
      }
    }

    updateCartCount(count) {
      // Update header cart count
      const cartCounts = document.querySelectorAll('[data-cart-count]');
      cartCounts.forEach(el => {
        el.textContent = count;
        el.hidden = count === 0;
      });
    }

    showEmptyCart() {
      // r71: [data-cart-content]/[data-cart-empty] don't both exist in
      // main-cart.liquid (the empty div only renders server-side) — removing
      // the last item left the checkout shell at 0,00 €. Reload renders the
      // real empty state.
      if (!this.cartContent || !this.emptyMessage) { window.location.reload(); return; }
      this.cartContent.hidden = true;
      this.emptyMessage.hidden = false;
    }

    // The same output as snippets/money-suffix.liquid ("2.250,00 €", non-breaking space) - r189: this
    // printed "2250,00 €" once a + or - redrew the amounts.
    formatMoney(cents) {
      const [whole, dec] = (Math.round(Number(cents) || 0) / 100).toFixed(2).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + dec + '\u00a0€';
    }
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new CartPage());
  } else {
    new CartPage();
  }
})();
